import { useEffect, useRef, useState } from 'react';
import { MapPin } from 'lucide-react';

/**
 * Le champ « numéro et voie », complété par la Base Adresse Nationale.
 *
 * L'adresse tapée à la main est la première cause de colis perdu : une voie
 * mal orthographiée, un code postal d'à côté. La BAN — le référentiel public
 * des adresses françaises, gratuit et sans clé — propose au fil de la frappe,
 * et un choix remplit d'un coup la voie, le code postal et la commune.
 *
 * Elle propose, elle n'impose pas : une adresse toute neuve ou un lieu-dit
 * qu'elle ignore se saisit librement, exactement comme avant. Et si le
 * service ne répond pas, le champ reste un champ — l'acheteur ne doit jamais
 * être bloqué par un annuaire en panne.
 */
export default function ChampAdresse({
  valeur,
  onChange,
  onAdresseChoisie,
  placeholder = 'Numéro et voie *',
}: {
  valeur: string;
  onChange: (valeur: string) => void;
  /** Une proposition retenue : la voie, et le couple code postal / commune. */
  onAdresseChoisie: (adresse: { ligne1: string; codePostal: string; ville: string }) => void;
  placeholder?: string;
}) {
  const [propositions, setPropositions] = useState<
    { libelle: string; ligne1: string; codePostal: string; ville: string }[]
  >([]);
  const [ouverte, setOuverte] = useState(false);
  const [surbrillance, setSurbrillance] = useState(-1);
  const minuterie = useRef<ReturnType<typeof setTimeout> | null>(null);
  const conteneur = useRef<HTMLDivElement>(null);

  // Le clic ailleurs referme la liste — sans annuler ce qui est tapé.
  useEffect(() => {
    const surClic = (e: MouseEvent) => {
      if (conteneur.current && !conteneur.current.contains(e.target as Node)) {
        setOuverte(false);
      }
    };
    document.addEventListener('mousedown', surClic);

    return () => document.removeEventListener('mousedown', surClic);
  }, []);

  const chercher = (texte: string) => {
    onChange(texte);
    setSurbrillance(-1);

    if (minuterie.current) clearTimeout(minuterie.current);

    // En dessous de cinq caractères, la BAN répond du bruit ; et sans
    // temporisation, chaque frappe est une requête vers un service public
    // qu'on n'a pas à marteler.
    if (texte.trim().length < 5) {
      setPropositions([]);
      setOuverte(false);

      return;
    }

    minuterie.current = setTimeout(async () => {
      try {
        const reponse = await fetch(
          'https://data.geopf.fr/geocodage/search/?limit=5&autocomplete=1&q=' +
            encodeURIComponent(texte.trim())
        );
        if (!reponse.ok) throw new Error();
        const donnees = await reponse.json();

        const lues = (donnees.features ?? [])
          .map((f: any) => f.properties ?? {})
          // Une commune seule ne remplit pas une étiquette : on ne garde que
          // ce qui porte une voie.
          .filter((p: any) => p.type === 'housenumber' || p.type === 'street')
          .map((p: any) => ({
            libelle: p.label ?? '',
            ligne1: p.name ?? '',
            codePostal: p.postcode ?? '',
            ville: p.city ?? '',
          }))
          .filter((p: any) => p.ligne1 && p.codePostal && p.ville);

        setPropositions(lues);
        setOuverte(lues.length > 0);
      } catch {
        // L'annuaire en panne n'est pas un problème de l'acheteur : la
        // saisie libre continue, sans message.
        setPropositions([]);
        setOuverte(false);
      }
    }, 300);
  };

  const choisir = (p: { ligne1: string; codePostal: string; ville: string }) => {
    onAdresseChoisie(p);
    setOuverte(false);
    setPropositions([]);
  };

  return (
    <div ref={conteneur} className="relative">
      <input
        className="input w-full"
        placeholder={placeholder}
        value={valeur}
        onChange={(e) => chercher(e.target.value)}
        onFocus={() => propositions.length > 0 && setOuverte(true)}
        onKeyDown={(e) => {
          if (!ouverte) return;
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            setSurbrillance((s) => Math.min(s + 1, propositions.length - 1));
          } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setSurbrillance((s) => Math.max(s - 1, 0));
          } else if (e.key === 'Enter' && surbrillance >= 0) {
            e.preventDefault();
            choisir(propositions[surbrillance]);
          } else if (e.key === 'Escape') {
            setOuverte(false);
          }
        }}
        autoComplete="off"
        role="combobox"
        aria-expanded={ouverte}
        aria-autocomplete="list"
      />

      {ouverte && (
        <ul
          role="listbox"
          className="absolute z-20 left-0 right-0 mt-1 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-lg overflow-hidden"
        >
          {propositions.map((p, i) => (
            <li key={p.libelle + i} role="option" aria-selected={i === surbrillance}>
              <button
                type="button"
                // mousedown et non click : le blur du champ fermerait la
                // liste avant que le clic n'atteigne la ligne.
                onMouseDown={(e) => {
                  e.preventDefault();
                  choisir(p);
                }}
                onMouseEnter={() => setSurbrillance(i)}
                className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 ${
                  i === surbrillance
                    ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-800 dark:text-primary-200'
                    : 'text-gray-700 dark:text-gray-300'
                }`}
              >
                <MapPin className="w-3.5 h-3.5 shrink-0 text-gray-400" />
                {p.libelle}
              </button>
            </li>
          ))}
          <li className="px-3 py-1.5 text-xs text-gray-400 border-t border-gray-100 dark:border-gray-800">
            Adresse absente ? Continuez à taper : la saisie libre suffit.
          </li>
        </ul>
      )}
    </div>
  );
}
